import type { ExpressionSpecification } from "@maplibre/maplibre-gl-style-spec";
import type { Feature, FeatureCollection, Point } from "geojson";
import { TH_LEVELS } from "../thermo/thermo";

/** Puntos por vista de mapa (sin PostGIS todavía: limita el payload, no acelera la consulta). */
export const MAP_POINT_LIMIT = 2000;

export type MapPoint = {
  id: number;
  lat: number | null;
  lng: number | null;
  nombre: string;
  thermo_base: number;
  /** solo en paradas de ruta: null si todavía no se promovió a empresa del CRM */
  company_id?: number | null;
};

export type MapFeatureProps = {
  id: number;
  nombre: string;
  thermo_base: number;
};

/** Filas con lat/lng → FeatureCollection para la fuente GeoJSON del mapa. */
export const toFeatureCollection = (
  rows: MapPoint[],
): FeatureCollection<Point, MapFeatureProps> => ({
  type: "FeatureCollection",
  features: rows
    .filter(
      (r): r is MapPoint & { lat: number; lng: number } =>
        Number.isFinite(r.lat) && Number.isFinite(r.lng),
    )
    .map(
      (r): Feature<Point, MapFeatureProps> => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [r.lng, r.lat] },
        properties: { id: r.id, nombre: r.nombre, thermo_base: r.thermo_base },
      }),
    ),
});

const isValidPoint = (
  r: MapPoint,
): r is MapPoint & { lat: number; lng: number } =>
  typeof r.lat === "number" &&
  typeof r.lng === "number" &&
  Number.isFinite(r.lat) &&
  Number.isFinite(r.lng) &&
  Math.abs(r.lat) <= 90 &&
  Math.abs(r.lng) <= 180;

const SINGLE_POINT_ZOOM = 12;

export type MapCameraTarget =
  | { kind: "none" }
  | { kind: "single"; center: [number, number]; zoom: number }
  | { kind: "multi"; bounds: [[number, number], [number, number]] };

/** A dónde debe apuntar la cámara para el conjunto de puntos recibido (solo lat/lng válidos). */
export const mapCameraTarget = (rows: MapPoint[]): MapCameraTarget => {
  const valid = rows.filter(isValidPoint);
  if (valid.length === 0) return { kind: "none" };
  if (valid.length === 1) {
    const { lat, lng } = valid[0];
    return { kind: "single", center: [lng, lat], zoom: SINGLE_POINT_ZOOM };
  }
  const lats = valid.map((r) => r.lat);
  const lngs = valid.map((r) => r.lng);
  return {
    kind: "multi",
    bounds: [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ],
  };
};

/** Resuelve una variable CSS (`--primary`, …) a un color real — MapLibre no entiende `var()`. */
export const cssColor = (cssVar: string) => {
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(cssVar)
    .trim();
  return v || "#888888";
};

/** Expresión "step" de color por termómetro, misma escala que `TH_LEVELS` (thermo.tsx). */
export const thermoStepExpression = (
  colorOf: (cssVar: string) => string,
): ExpressionSpecification => {
  const asc = [...TH_LEVELS].sort((a, b) => a[0] - b[0]);
  const [base, ...rest] = asc;
  return [
    "step",
    ["get", "thermo_base"],
    colorOf(base[2]),
    ...rest.flatMap(([threshold, , cssVar]) => [threshold, colorOf(cssVar)]),
  ] as ExpressionSpecification;
};
